import { Paper, CardContent, Typography, Link, Box, CircularProgress } from "@mui/material";
import { AuthorAnnualMetrics, RetractedAuthor } from "../CommonInterfaces";
import { useRef, useState, useCallback, useEffect } from "react";
import { auth } from "../../firebase/firebase";
import { argosApiEntryPoint } from "../../GlobalConfig";
import AuthorAnnualMetricsBarChart from "./AuthorAnnualMetricsBarChart";

interface AuthorItemProps {
    authorId: string;
}

interface AuthorSummary {
    authorId: string;
    totalAnalysed: number;
    totalRetracted: number;
    totalExpressionsOfConcern: number;
    totalHighRisk: number;
    totalMediumRisk: number;
    totalLowRiskImpacted: number;
    annualScholarlyProduction: AuthorAnnualMetrics[];
}


const AuthorItem: React.FC<AuthorItemProps> = ({ authorId }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [data, setData] = useState<AuthorSummary | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let authToken = await auth.currentUser?.getIdToken(true)
            const response = await fetch(argosApiEntryPoint + '/stats/author-summary?authorId=' + authorId, {
                method: 'GET',
                headers: {
                    'content-type': 'application/json;charset=UTF-8',
                    'Authorization': 'Bearer ' + authToken
                }
            });
            if (!response.ok) throw new Error("Failed to fetch /info");
            const json: AuthorSummary = await response.json();
            setData(json);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !data && !loading) {
                    fetchData();
                }
            },
            { threshold: 0.5 }
        );

        const current = ref.current;
        if (current) observer.observe(current);

        return () => {
            if (current) observer.unobserve(current);
        };
    }, [data, fetchData, loading]);

    return (
        <Box ref={ref}>
            {loading ? (
                <CircularProgress />
            ) : data ? (
                <>
                    <Box>
                        <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>Articles since 2014: {data.totalAnalysed}</Typography>
                        <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>Retracted: {data.totalRetracted}</Typography>
                        <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>Expressions of Concern: {data.totalExpressionsOfConcern}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>High Risk: {data.totalHighRisk}</Typography>
                        <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>Medium Risk: {data.totalMediumRisk}</Typography>
                    </Box>
                    <AuthorAnnualMetricsBarChart data={data.annualScholarlyProduction} />
                </>
            ) : (
                <Typography color="textSecondary">Scroll to load</Typography>
            )
            }
        </Box >
    );
};

interface AuthorHeaderProps {
    author: RetractedAuthor;
    index: number;
}

function AuthorHeader({ author, index }: AuthorHeaderProps) {

    const handleLinkClick = (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevents triggering Box onClick
    };

    return <Paper sx={{ margin: 2 }}>
        <CardContent sx={{ alignContent: 'initial' }} key={'author_' + index}>
            <Typography gutterBottom variant="body2" component="div">
                <Typography variant="body2" style={{ fontWeight: 'bolder' }}>{author.displayName}
                    {author.ids.openAlexId !== null &&
                        <Link href={author.ids.openAlexId} onClick={handleLinkClick} target="_blank"><img src="/argos/static/images/logos/openalex_logo.png" width='20px' /></Link>
                    }
                    {author.ids.orcid !== null &&
                        <Link href={author.ids.orcid} onClick={handleLinkClick} target="_blank"><img src="/argos/static/images/ORCID_iD.svg" width='20px' /></Link>
                    }
                </Typography>
            </Typography>
            <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }}>
                Institutions ({author.institutions.filter((it: string) => it.length > 0).length}): {author.institutions.join(", ")}
            </Typography>
            <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}> Works count: {author.worksCount} </Typography>
            <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span' sx={{ marginRight: 2 }}>
                Cited by: {author.citedByCount}</Typography>
            <Typography variant="body2" style={{ color: '#414e59', fontSize: 10 }} component='span'>
                H-Index: {author.hIndex}</Typography>
            <AuthorItem key={index} authorId={author.ids.openAlexId} />
        </CardContent >
    </Paper >
}

export default AuthorHeader;